import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { logAudit } from '../lib/audit';

export interface Plan {
  id: string;
  name: string;
  slug: string;
  accounts_limit: number | null;
  cards_limit: number | null;
  monthly_transactions_limit: number | null;
  goals_limit: number | null;
  reports_enabled: boolean;
  price_brl: number;
  price_monthly: number;
  price_annual: number;
  annual_discount_percent: number;
  /* Feature flags */
  can_export_csv: boolean;
  can_export_pdf: boolean;
  can_access_advanced_insights: boolean;
  can_use_multiple_currencies: boolean;
  can_access_loans_module: boolean;
  can_access_recurring_advanced: boolean;
  can_access_priority_support: boolean;
  /* Insights module */
  can_access_insights_module: boolean;
  insights_level: 'basic' | 'advanced' | 'full';
  max_insights_per_period: number;
  can_access_historical_insights: boolean;
  can_access_recommendations: boolean;
  /* Billing */
  monthly_billing_enabled: boolean;
  annual_billing_enabled: boolean;
  auto_renewal_default: boolean;
  grace_days: number;
  overdue_behavior: string;
  /* Commercial */
  is_featured: boolean;
  is_recommended: boolean;
  is_best_value: boolean;
  promotional_text: string;
  description: string;
  commercial_description: string;
  trial_available: boolean;
  trial_days: number;
  /* Payees module */
  can_use_payees_module: boolean;
  payees_limit: number | null;
  /* AI Assistant */
  can_use_ai_assistant: boolean;
  ai_monthly_questions_limit: number | null;
  can_use_ai_advanced_insights: boolean;
  can_use_ai_proactive_alerts: boolean;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: string;
  subscription_type: string;
  amount: number;
  billing_cycle: string;
  started_at: string;
  expires_at: string | null;
  auto_renew: boolean;
  created_at: string;
  plans: Plan;
}

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('subscriptions')
      .select('*, plans(*)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setSubscription(data as Subscription | null);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const upgradePlan = useCallback(async (
    planId: string,
    amount: number,
    billingCycle: 'monthly' | 'annual' = 'monthly',
    forcePaid: boolean = false
  ): Promise<{ success: boolean; error?: string; plan?: string; isTrial?: boolean; trialDays?: number }> => {
    const { data, error } = await supabase.rpc('upgrade_plan_with_trial', {
      p_plan_id: planId,
      p_billing_cycle: billingCycle,
      p_force_paid: forcePaid,
    });
    if (error) {
      console.error('[useSubscription] upgradePlan error:', error);
      return { success: false, error: error.message };
    }
    const result = data as { success: boolean; error?: string; plan?: string; is_trial?: boolean; trial_days?: number; amount?: number };
    if (result.success) {
      await fetchSubscription();
      // Audit log for plan changes
      logAudit({
        action: result.is_trial ? 'create' : 'upgrade',
        resource: 'subscription',
        details: {
          plan: result.plan,
          billing_cycle: billingCycle,
          is_trial: result.is_trial,
          trial_days: result.trial_days,
          amount: result.amount,
        },
      });
    }
    return {
      success: result.success,
      error: result.error,
      plan: result.plan,
      isTrial: result.is_trial,
      trialDays: result.trial_days,
    };
  }, [fetchSubscription]);

  const isPaid = subscription?.status === 'active' && (
    // 'paid' = assinatura regular paga
    // 'lifetime' / 'vitalicio' = plano vitalício
    // 'complimentary' / 'manual' / 'gifted' = concedido manualmente pelo admin
    ['paid', 'lifetime', 'vitalicio', 'complimentary', 'manual', 'gifted', 'courtesy'].includes(
      subscription?.subscription_type ?? ''
    )
  );
  const isTrial = subscription?.subscription_type === 'trial';
  const isFree = !isPaid && !isTrial;
  const plan = subscription?.plans ?? null;

  return { subscription, plan, isPaid, isFree, isTrial, loading, refetch: fetchSubscription, upgradePlan };
};
